import React from 'react';
import {
  Button,
  VStack,
  Text,
  HStack,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { Stethoscope, Edit } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import DetailsSection from '@/components/ui/DetailsSection';
import RelativeTime from '@/components/ui/RelativeTime';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { BaseDetailsModalProps } from '@/types/DetailsModalTypes';
import { useGetSpecialityQuery } from '../api';

interface SpecialityDetailsModalProps extends BaseDetailsModalProps {
  // No additional props needed for SpecialityDetailsModal
}

const SpecialityDetailsModal: React.FC<SpecialityDetailsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  navigateToModal,
}) => {
  // Fetch individual entity if entityId is provided (hash modal mode)
  const { data: selectedSpeciality, isLoading } = useGetSpecialityQuery(entityId!, {
    skip: !entityId,
  });

  const renderContent = () => {
    if (isLoading) {
      return <Text color="gray.500">Loading details...</Text>;
    }

    if (!selectedSpeciality) {
      return <Text>Speciality not found</Text>;
    }

    return (
      <VStack gap={6} align="stretch">
        {/* Basic Information */}
        <DetailsSection
          title="Basic Information"
          icon={Stethoscope}
          items={[
            {
              label: "ID",
              value: selectedSpeciality.id
            },
            {
              label: "Title",
              value: selectedSpeciality.title
            },
            {
              label: "Visibility",
              value: selectedSpeciality.is_shown ? 'shown' : 'hidden',
              render: () => (
                <Badge 
                  size="sm" 
                  colorPalette={selectedSpeciality.is_shown ? 'green' : 'red'}
                  variant="subtle"
                >
                  {selectedSpeciality.is_shown ? 'Shown' : 'Hidden'}
                </Badge>
              )
            },
            {
              label: "Created",
              value: selectedSpeciality.created_at,
              render: () => <RelativeTime date={selectedSpeciality.created_at} />
            },
            {
              label: "Updated",
              value: selectedSpeciality.updated_at,
              render: () => <RelativeTime date={selectedSpeciality.updated_at} />
            }
          ]}
        />
      </VStack>
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerHeader>
        <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
          Speciality Details {selectedSpeciality ? `- ${selectedSpeciality.title}` : ''}
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        {renderContent()}
      </DrawerBody>
      
      <DrawerFooter>
        <HStack justify="space-between" w="full">
          <HStack>
            {entityId && navigateToModal && (
              <Button 
                variant="outline" 
                onClick={() => navigateToModal(entityId, 'edit')}
              >
                <Icon as={Edit} boxSize={4} mr={2} />
                Edit
              </Button>
            )}
          </HStack>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Export wrapped version for hash modal (details modal: view action)
export default withHashDrawer(SpecialityDetailsModal, {
  action: 'view' // Default view action
}); 